import { useState } from 'react';
import { CardGrid } from '../../components/CardGrid';
import { ContentArea } from '../../components/ContentArea';
import { Dropdown } from '../../components/Dropdown';
import { EmptyMessage } from '../../components/EmptyMessage';
import { FilterPill, FilterPillGroup } from '../../components/FilterPill';
import { Section, Title } from '../../components/Section';
import { SkeletonCard } from '../../components/SkeletonCard';
import { SpaItemCard } from '../../composites/SpaItemCard';
import { CategoryDropdownContainer, FilterContainer, TitleRow } from './SpaListTemplate.styles';
import type { SpaListTemplateProps } from './SpaListTemplate.types';

export function SpaListTemplate({
  items,
  onSelectItem,
  isLoading,
  durationOptions,
  categoryOptions,
  selectedCategory,
  onCategoryChange,
  titleLabel = 'Spa days',
  fromPriceLabel = 'from',
  columns = 3,
  imageAspectRatio = '1 / 1',
  categoryLabel = 'Category',
  soldOutLabel = 'Sold out',
  editSearchLabel,
  onEditSearch,
  noResultsLabel = 'No spa days available.',
  noImageLabel = 'No image',
  formatGuests,
}: SpaListTemplateProps) {
  const [selectedDurations, setSelectedDurations] = useState<string[]>([]);

  const handleToggleDuration = (value: string) => {
    setSelectedDurations((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const filteredItems =
    selectedDurations.length === 0
      ? items
      : items.filter(
          (item) => item.durationValue && selectedDurations.includes(item.durationValue)
        );

  const filters = (
    <FilterContainer>
      {durationOptions && durationOptions.length > 0 && (
        <FilterPillGroup>
          {durationOptions.map((option) => (
            <FilterPill
              key={option.value}
              type="button"
              $active={selectedDurations.includes(option.value)}
              onClick={() => handleToggleDuration(option.value)}
            >
              {option.label}
            </FilterPill>
          ))}
        </FilterPillGroup>
      )}
      {categoryOptions && categoryOptions.length > 0 && onCategoryChange && (
        <CategoryDropdownContainer>
          <Dropdown
            options={categoryOptions}
            value={selectedCategory}
            placeholder={categoryLabel}
            onChange={onCategoryChange}
            variant="pill"
            aria-label={categoryLabel}
          />
        </CategoryDropdownContainer>
      )}
    </FilterContainer>
  );

  if (isLoading && items.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
          {filters}
        </TitleRow>
        <CardGrid $columns={columns}>
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonCard key={index} variant="grid" />
          ))}
        </CardGrid>
      </Section>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
          {filters}
        </TitleRow>
        <EmptyMessage>{noResultsLabel}</EmptyMessage>
      </Section>
    );
  }

  return (
    <Section>
      <TitleRow>
        <Title>{titleLabel}</Title>
        {filters}
      </TitleRow>

      <ContentArea>
        {filteredItems.length === 0 ? (
          <EmptyMessage>{noResultsLabel}</EmptyMessage>
        ) : (
          <CardGrid $columns={columns}>
            {filteredItems.map((item) => (
              <SpaItemCard
                key={item.id}
                id={item.id}
                name={item.name}
                imageUrl={item.imageUrl}
                imageAspectRatio={imageAspectRatio}
                price={item.price}
                discount={item.discount}
                duration={item.duration}
                minGuests={item.minGuests}
                maxGuests={item.maxGuests}
                unavailable={item.unavailable}
                promotionName={item.promotionName}
                fromPriceLabel={fromPriceLabel}
                soldOutLabel={soldOutLabel}
                editSearchLabel={editSearchLabel}
                onEditSearch={onEditSearch}
                noImageLabel={noImageLabel}
                formatGuests={formatGuests}
                onSelect={onSelectItem}
              />
            ))}
          </CardGrid>
        )}
      </ContentArea>
    </Section>
  );
}
